import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeftIcon, TrashIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { api, ApiError } from "../lib/api";
import { useToast } from "../components/Toast";
import { useBuchungsarten } from "../lib/receipts";

/**
 * Einstellungen › Jahreswerte (Block 4). Alles, was die Steuerrechnung außer Buchungen braucht: Gesetzeswerte (je Jahr neu), Unterlagen
 * (Lohnsteuerbescheinigung, EÜR, …; je Jahr neu) und Dauerwerte (Flächen, Anteile; werden übernommen). Dazu die Rollen der Buchungsarten
 * und die Anlagegüter. „fehlt“ heißt: nichts eingetragen — die Rechnung gilt dann als unvollständig.
 */
type Wert = { schluessel: string; label: string; art: "gesetz" | "unterlage" | "dauer"; typ: "zahl" | "datum" | "json" | "text"; wert: number | null; text: string | null; quelle: string | null; fehlt: boolean;
  vorjahr: { wert: number | null; text: string | null; quelle: string | null } | null };
type Rolle = { rolle: string; label: string; merkmal?: boolean; arten: { steuerArtId: string; name: string; merkmal: string | null }[] };
type Gut = { id: string; bezeichnung: string; einkunftsart: string; anschaffung: string; kostenCent: number; nutzungsdauer: number | null; anteil: number };
const ARTEN: { art: Wert["art"]; titel: string; text: string }[] = [
  { art: "unterlage", titel: "Unterlagen", text: "aus Lohnsteuerbescheinigung, EÜR, Bescheiden — jedes Jahr neu eintragen" },
  { art: "dauer", titel: "Dauerwerte", text: "Flächen, Anteile, Anschaffung — werden ins Folgejahr übernommen" },
  { art: "gesetz", titel: "Gesetzeswerte", text: "Tarif, Pauschbeträge — jedes Jahr neu aus dem Gesetz (mit Fundstelle)" },
];
const feld = "border border-ios-line rounded-lg px-2 py-1.5 text-[16px] lg:text-[14px] bg-white";
const fehlertext = (e: unknown) => (e instanceof ApiError ? e.message : "Speichern fehlgeschlagen");

export function JahreswertePage() {
  const [sp, setSp] = useSearchParams();
  const jahr = Number(sp.get("jahr")) || new Date().getFullYear();
  const qc = useQueryClient();
  const toast = useToast();
  const { data } = useQuery<Wert[]>({ queryKey: ["jahreswerte", jahr], queryFn: () => api.get(`/api/jahreswerte/${jahr}`) });
  const uebernehmen = useMutation({
    mutationFn: () => api.post<{ rollen: number; werte: number }>(`/api/jahreswerte/${jahr}/uebernehmen`, { von: jahr - 1 }),
    onSuccess: (r) => { toast.success(`${r.werte} Dauerwerte und ${r.rollen} Rollen aus ${jahr - 1} übernommen`); void qc.invalidateQueries(); },
    onError: (e) => toast.error(fehlertext(e)),
  });
  const fehlend = (data ?? []).filter((w) => w.fehlt).length;
  return (
    <div className="max-w-3xl mx-auto p-4 lg:p-6">
      <Link to="/einstellungen" className="inline-flex items-center gap-1 text-ios-blue text-[14px] mb-3"><ChevronLeftIcon className="w-4 h-4" /> Einstellungen</Link>
      <div className="flex items-baseline gap-3 mb-1">
        <h1 className="text-[22px] font-semibold tracking-tight flex-1">Jahreswerte</h1>
        <input className={`${feld} w-24`} inputMode="numeric" aria-label="Steuerjahr" value={jahr} onChange={(e) => setSp({ jahr: e.target.value }, { replace: true })} />
      </div>
      <p className="text-[13px] text-ios-muted mb-3">
        {fehlend ? `${fehlend} Werte fehlen für ${jahr}. ` : `Alle Werte für ${jahr} sind eingetragen. `}
        Ein neues Jahr übernimmt Dauerwerte und Rollen beim ersten Öffnen automatisch aus dem Vorjahr. Unterlagen und Gesetzeswerte gelten je Jahr neu —
        der Vorjahreswert steht als Vorschlag daneben und zählt erst nach „Speichern“.
      </p>
      <button type="button" disabled={uebernehmen.isPending} onClick={() => uebernehmen.mutate()} className="mb-4 px-3 py-1.5 rounded-ios bg-white shadow-card text-ios-blue text-[14px]">
        Dauerwerte und Rollen aus {jahr - 1} übernehmen
      </button>
      <div className="space-y-4">
        <FlaechenStaende />
        {ARTEN.map((a) => (
          <section key={a.art} className="bg-white rounded-iosLg shadow-card p-4">
            <h2 className="text-[15px] font-medium">{a.titel}</h2>
            <p className="text-[12px] text-ios-muted mb-2">{a.text}</p>
            <div className="divide-y divide-ios-line">
              {(data ?? []).filter((w) => w.art === a.art).map((w) => <WertZeile key={w.schluessel} w={w} jahr={jahr} />)}
            </div>
          </section>
        ))}
        <Kredite jahr={jahr} />
        <Beteiligungen jahr={jahr} />
        <Rollen jahr={jahr} />
        <Anlagegueter />
      </div>
    </div>
  );
}

/** Flächen mit „gültig ab“ (Modell B): ab 2025 die einzige Quelle für Flächen und Garten-Parteien. Korrektur = Datum am Anfang des Zeitraums,
 *  echte Änderung (Umzug, neue Mietpartei) = Datum der Änderung. Vor dem Speichern zeigt „Auswirkung prüfen“, welche bestätigten Buchungen sich
 *  ändern und was das am Ergebnis je Jahr bewirkt. */
type Stand = { id: string; gueltigAb: string; gesamt: number; vermietet: number; arbeitszimmer: number; gartenParteien: number | null; gartenMietparteien: number | null; quelle: string };
const LEER: Omit<Stand, "id"> = { gueltigAb: "", gesamt: 0, vermietet: 0, arbeitszimmer: 0, gartenParteien: null, gartenMietparteien: null, quelle: "" };
function FlaechenStaende() {
  const { data } = useQuery<Stand[]>({ queryKey: ["flaechen-staende"], queryFn: () => api.get("/api/flaechen-staende") });
  return (
    <section className="bg-white rounded-iosLg shadow-card p-4">
      <h2 className="text-[15px] font-medium">Flächen</h2>
      <p className="text-[12px] text-ios-muted mb-2">Gilt ab dem eingetragenen Datum bis zum nächsten Stand. Eine Korrektur trägst du mit dem Datum ein, ab dem sie
        schon immer galt; eine echte Änderung (Umzug, neue Mietpartei) mit dem Datum der Änderung. Bestätigte Buchungen, die sich dadurch ändern, stehen danach auf „geändert“.</p>
      <div className="divide-y divide-ios-line">
        {(data ?? []).map((s) => <StandZeile key={s.id} s={s} />)}
        <StandZeile s={null} />
      </div>
    </section>
  );
}
function StandZeile({ s }: { s: Stand | null }) {
  const qc = useQueryClient(), toast = useToast();
  const [offen, setOffen] = useState(false);
  const [f, setF] = useState<Omit<Stand, "id">>(s ?? LEER);
  const [vs, setVs] = useState<{ jahr: number; geaendert: number; ergebnisVorher: number; ergebnisNachher: number }[] | null>(null);
  const zahl = (v: string) => (v.trim() === "" ? null : Number(v.replace(",", ".")));
  const koerper = () => ({ ...(s ? { id: s.id } : {}), ...f });
  const neu = () => { void qc.invalidateQueries(); setVs(null); };
  const pruefen = useMutation({ mutationFn: () => api.post<typeof vs>("/api/flaechen-staende/vorschau", koerper()), onSuccess: (r) => setVs(r), onError: (e) => toast.error(fehlertext(e)) });
  const speichern = useMutation({ mutationFn: () => api.put("/api/flaechen-staende", koerper()),
    onSuccess: () => { toast.success("Flächen gespeichert"); neu(); if (!s) { setF(LEER); setOffen(false); } }, onError: (e) => toast.error(fehlertext(e)) });
  const loeschen = useMutation({ mutationFn: () => api.del(`/api/flaechen-staende/${s!.id}`), onSuccess: () => { toast.success("Stand gelöscht"); neu(); }, onError: (e) => toast.error(fehlertext(e)) });
  const de = (n: number | null) => (n === null ? "" : String(n).replace(".", ","));
  const eur = (x: number) => x.toLocaleString("de-DE", { style: "currency", currency: "EUR" });
  if (!s && !offen) return <div className="py-2"><button type="button" className="text-ios-blue text-[14px]" onClick={() => setOffen(true)}>+ neuer Stand</button></div>;
  const eingabe = (k: keyof Omit<Stand, "id" | "gueltigAb" | "quelle">, label: string) => (
    <label className="text-[12px] text-ios-muted flex flex-col">{label}
      <input className={`${feld} w-24`} inputMode="decimal" value={de(f[k] as number | null)} onChange={(e) => { setVs(null); setF({ ...f, [k]: zahl(e.target.value) ?? (k.startsWith("garten") ? null : 0) }); }} /></label>);
  return (
    <div className="py-3 space-y-2 text-[14px]">
      <div className="flex flex-wrap gap-2 items-end">
        <label className="text-[12px] text-ios-muted flex flex-col">gültig ab
          <input type="date" className={feld} value={f.gueltigAb} onChange={(e) => { setVs(null); setF({ ...f, gueltigAb: e.target.value }); }} /></label>
        {eingabe("gesamt", "Wohnfläche m²")}{eingabe("vermietet", "davon vermietet m²")}{eingabe("arbeitszimmer", "davon Arbeitszimmer m²")}
        {eingabe("gartenParteien", "Garten: Parteien")}{eingabe("gartenMietparteien", "davon Mietparteien")}
      </div>
      <input className={`${feld} w-full`} placeholder="Herkunft (Pflicht), z. B. Mietvertrag, Grundriss" value={f.quelle} onChange={(e) => setF({ ...f, quelle: e.target.value })} />
      <div className="flex flex-wrap gap-2 items-center">
        <button type="button" className="px-3 py-1.5 rounded-ios bg-white shadow-card text-ios-blue" disabled={pruefen.isPending} onClick={() => pruefen.mutate()}>Auswirkung prüfen</button>
        <button type="button" className="px-3 py-1.5 rounded-ios bg-ios-blue text-white" disabled={speichern.isPending} onClick={() => speichern.mutate()}>Speichern</button>
        {s && <button type="button" aria-label="Stand löschen" className="text-ios-subtle" onClick={() => loeschen.mutate()}><TrashIcon className="w-4 h-4" /></button>}
      </div>
      {vs && (vs.length === 0 ? <p className="text-[13px] text-ios-muted">Keine Buchungen ab 2025 betroffen.</p>
        : <ul className="text-[13px] space-y-0.5">{vs.map((v) => <li key={v.jahr}>{v.jahr}: {v.geaendert} bestätigte Buchungen ändern sich · Erstattung/Nachzahlung {eur(v.ergebnisVorher)} → {eur(v.ergebnisNachher)}</li>)}</ul>)}
    </div>
  );
}

function WertZeile({ w, jahr }: { w: Wert; jahr: number }) {
  const qc = useQueryClient();
  const toast = useToast();
  const start = w.typ === "zahl" ? (w.wert === null ? "" : String(w.wert).replace(".", ",")) : (w.text ?? "");
  const [v, setV] = useState(start);
  const [q, setQ] = useState(w.quelle ?? "");
  const speichern = useMutation({
    mutationFn: () => api.put(`/api/jahreswerte/${jahr}/${encodeURIComponent(w.schluessel)}`,
      w.typ === "zahl" ? { wert: Number(v.replace(/\./g, "").replace(",", ".")), quelle: q } : { text: v, quelle: q }),
    onSuccess: () => { toast.success("gespeichert"); void qc.invalidateQueries({ queryKey: ["jahreswerte", jahr] }); void qc.invalidateQueries({ queryKey: ["steuer"] }); },
    onError: (e) => toast.error(fehlertext(e)),
  });
  const geaendert = v !== start || q !== (w.quelle ?? "");
  return (
    <div className="py-2 text-[14px]">
      <div className="flex items-center gap-2">
        <span className="flex-1">{w.label}</span>
        {w.fehlt && <span className="text-[12px] px-2 py-0.5 rounded-full bg-ios-blue/10 text-ios-blue">fehlt</span>}
      </div>
      {w.fehlt && w.vorjahr && (
        <div className="text-[12px] text-ios-muted mt-1">
          {jahr - 1}: {w.typ === "zahl" ? String(w.vorjahr.wert ?? "").replace(".", ",") : w.vorjahr.text}{w.vorjahr.quelle ? ` (${w.vorjahr.quelle})` : ""}{" "}
          {/* Nur Gesetzeswerte einsetzbar (oft unverändert) — und nur der Wert: die Fundstelle für das neue Jahr trägt der Mensch selbst ein.
              Unterlagen (Lohnsteuerbescheinigung, EÜR …) ändern sich jedes Jahr → nur zur Information, nie vorbelegen. */}
          {w.art === "gesetz" && <button type="button" className="text-ios-blue" onClick={() =>
            setV(w.typ === "zahl" ? String(w.vorjahr!.wert ?? "").replace(".", ",") : (w.vorjahr!.text ?? ""))}>Wert einsetzen (Fundstelle {jahr} selbst eintragen)</button>}
        </div>
      )}
      <div className="flex flex-wrap gap-2 mt-1">
        {w.typ === "json"
          ? <textarea className={clsx(feld, "flex-1 min-w-0 font-mono text-[12px]")} rows={2} value={v} onChange={(e) => setV(e.target.value)} />
          : <input className={clsx(feld, "w-40")} value={v} placeholder={w.typ === "datum" ? "JJJJ-MM-TT" : ""} inputMode={w.typ === "zahl" ? "decimal" : "text"} onChange={(e) => setV(e.target.value)} />}
        <input className={clsx(feld, "flex-1 min-w-[10rem] text-[13px]")} value={q} placeholder="Quelle (Unterlage, Fundstelle)" onChange={(e) => setQ(e.target.value)} />
        <button type="button" disabled={!geaendert || !v || speichern.isPending} onClick={() => speichern.mutate()} className="px-3 py-1 rounded-ios bg-ios-blue text-white disabled:opacity-40">Speichern</button>
      </div>
    </div>
  );
}

function Rollen({ jahr }: { jahr: number }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { data } = useQuery<Rolle[]>({ queryKey: ["rechen-rollen", jahr], queryFn: () => api.get(`/api/rechen-rollen/${jahr}`) });
  const { data: artDaten } = useBuchungsarten(jahr);
  const setzen = useMutation({
    mutationFn: (x: { rolle: string; arten: { steuerArtId: string; merkmal: string | null }[] }) =>
      api.put(`/api/rechen-rollen/${jahr}/${encodeURIComponent(x.rolle)}`, { arten: x.arten }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["rechen-rollen", jahr] }); void qc.invalidateQueries({ queryKey: ["steuer"] }); },
    onError: (e) => toast.error(fehlertext(e)),
  });
  const arten = artDaten?.arten ?? [];
  const [merkmale, setMerkmale] = useState<Record<string, string>>({});
  return (
    <section className="bg-white rounded-iosLg shadow-card p-4">
      <h2 className="text-[15px] font-medium">Rollen der Buchungsarten</h2>
      <p className="text-[12px] text-ios-muted mb-2">Welche Buchungsarten die Rechnung an welcher Stelle zählt. Jede Art mit Buchungen braucht eine Rolle (auch „ohne Wirkung“ mit Grund).</p>
      <div className="divide-y divide-ios-line">
        {(data ?? []).map((r) => (
          <div key={r.rolle} className="py-2 text-[14px]">
            <div>{r.label}</div>
            <div className="flex flex-wrap gap-1 mt-1">
              {r.arten.map((a) => (
                <span key={a.steuerArtId + (a.merkmal ?? "")} className="inline-flex items-center gap-1 text-[12px] px-2 py-0.5 rounded-full bg-ios-bg">
                  {a.name}{a.merkmal ? ` · Merkmal „${a.merkmal}“` : ""}
                  <button type="button" aria-label="entfernen" className="text-ios-subtle"
                    onClick={() => setzen.mutate({ rolle: r.rolle, arten: r.arten.filter((x) => x !== a).map((x) => ({ steuerArtId: x.steuerArtId, merkmal: x.merkmal })) })}>×</button>
                </span>
              ))}
              {r.merkmal && <input className={clsx(feld, "text-[12px] py-0.5 w-40")} placeholder="Merkmal (Pflicht)" aria-label="Merkmal"
                value={merkmale[r.rolle] ?? ""} onChange={(e) => setMerkmale({ ...merkmale, [r.rolle]: e.target.value })} />}
              <select className={clsx(feld, "text-[12px] py-0.5")} value="" aria-label="Art hinzufügen" disabled={r.merkmal && !(merkmale[r.rolle] ?? "").trim()}
                onChange={(e) => e.target.value && setzen.mutate({ rolle: r.rolle, arten: [...r.arten.map((x) => ({ steuerArtId: x.steuerArtId, merkmal: x.merkmal })),
                  { steuerArtId: e.target.value, merkmal: r.merkmal ? (merkmale[r.rolle] ?? "").trim() : null }] })}>
                <option value="">+ Art</option>
                {arten.filter((a) => !r.arten.some((x) => x.steuerArtId === a.id)).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Anlagegueter() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data } = useQuery<Gut[]>({ queryKey: ["anlagegueter"], queryFn: () => api.get("/api/anlagegueter") });
  const [n, setN] = useState({ bezeichnung: "", einkunftsart: "§19", anschaffung: "", kosten: "", nutzungsdauer: "", anteil: "100" });
  const neu = useMutation({
    mutationFn: () => api.post("/api/anlagegueter", { bezeichnung: n.bezeichnung, einkunftsart: n.einkunftsart, anschaffung: n.anschaffung,
      kostenCent: Math.round(Number(n.kosten.replace(/\./g, "").replace(",", ".")) * 100), nutzungsdauer: n.nutzungsdauer ? Number(n.nutzungsdauer) : null,
      anteil: Number(n.anteil.replace(",", ".")) / 100 }),
    onSuccess: () => { setN({ ...n, bezeichnung: "", kosten: "", anschaffung: "" }); void qc.invalidateQueries({ queryKey: ["anlagegueter"] }); void qc.invalidateQueries({ queryKey: ["steuer"] }); },
    onError: (e) => toast.error(fehlertext(e)),
  });
  const weg = useMutation({ mutationFn: (id: string) => api.del(`/api/anlagegueter/${id}`), onSuccess: () => { void qc.invalidateQueries({ queryKey: ["anlagegueter"] }); void qc.invalidateQueries({ queryKey: ["steuer"] }); } });
  return (
    <section className="bg-white rounded-iosLg shadow-card p-4 text-[14px]">
      <h2 className="text-[15px] font-medium">Anlagegüter (bewegliche Wirtschaftsgüter)</h2>
      <p className="text-[12px] text-ios-muted mb-2">AfA über die Nutzungsdauer, monatsgenau; bis zur GWG-Grenze Sofortabzug im Anschaffungsjahr.</p>
      {(data ?? []).map((g) => (
        <div key={g.id} className="flex items-center gap-2 py-1">
          <span className="flex-1">{g.bezeichnung} · {g.einkunftsart} · {g.anschaffung} · {(g.kostenCent / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
            {g.nutzungsdauer ? ` · ${g.nutzungsdauer} J.` : ""}{g.anteil !== 1 ? ` · ${Math.round(g.anteil * 100)} %` : ""}</span>
          <button type="button" aria-label="löschen" className="text-ios-subtle" onClick={() => weg.mutate(g.id)}><TrashIcon className="w-4 h-4" /></button>
        </div>
      ))}
      {(data ?? []).length === 0 && <p className="text-[13px] text-ios-muted">Keine Anlagegüter.</p>}
      <div className="flex flex-wrap gap-2 mt-2">
        <input className={clsx(feld, "flex-1 min-w-[8rem]")} placeholder="Bezeichnung" value={n.bezeichnung} onChange={(e) => setN({ ...n, bezeichnung: e.target.value })} />
        <select className={feld} value={n.einkunftsart} onChange={(e) => setN({ ...n, einkunftsart: e.target.value })}>
          <option value="§19">§ 19</option><option value="§21">§ 21</option><option value="§18 neutral">§ 18 (Lexoffice)</option>
        </select>
        <input className={clsx(feld, "w-32")} placeholder="JJJJ-MM-TT" value={n.anschaffung} onChange={(e) => setN({ ...n, anschaffung: e.target.value })} />
        <input className={clsx(feld, "w-24")} placeholder="Kosten €" inputMode="decimal" value={n.kosten} onChange={(e) => setN({ ...n, kosten: e.target.value })} />
        <input className={clsx(feld, "w-20")} placeholder="Jahre" inputMode="numeric" value={n.nutzungsdauer} onChange={(e) => setN({ ...n, nutzungsdauer: e.target.value })} />
        <input className={clsx(feld, "w-20")} placeholder="Anteil %" inputMode="decimal" value={n.anteil} onChange={(e) => setN({ ...n, anteil: e.target.value })} />
        <button type="button" disabled={neu.isPending} onClick={() => neu.mutate()} className="px-3 py-1 rounded-ios bg-ios-blue text-white">hinzufügen</button>
      </div>
    </section>
  );
}

// ── Kredit-Konten (24.10.2026) ───────────────────────────────────────────────────────────────────────────────────────────
// Jeder Kredit ist ein Konto der Art „Kredit“ (anlegen unter Einstellungen › Konten). Hier: woher die Zinsen kommen, ob er durch die
// Grundschuld gesichert ist, und je Jahr der hausbezogene Anteil sowie — ohne Kontoauszug — die Zinsen laut Tilgungsplan.
type Kredit = { kontoId: string; name: string; zinsQuelle: "buchungen" | "tilgungsplan" | null; grundschuld: boolean; aktiv: boolean;
  hausbezug: number | null; hausbezugQuelle: string | null; zinsen: number | null; zinsenQuelle: string | null;
  kreditBeginn: string | null; kreditLaufzeitMonate: number | null; abgeloestAm: string | null; schlusszinsen: number | null; schlusszinsenQuelle: string | null;
  zeitraum: { beginn: string | null; ende: string | null };
  plaene: { id: string; gueltigAb: string; quelle: string; summeZinsen: number }[]; planZinsJahr: number | null };
type PlanVorschau = { aenderungen?: { jahr: number; vorher: number; nachher: number }[]; zeilen: number; erste: { datum: string } | null; letzte: { datum: string } | null; summeZinsen: number; summeTilgung: number;
  gedruckt: { zinsenCent: number | null; nettoCent: number | null }; fehler: string[]; jeJahr: Record<string, { raten: number; zinsCent: number }> };
const eur = (x: number) => x.toLocaleString("de-DE", { style: "currency", currency: "EUR" });
function Kredite({ jahr }: { jahr: number }) {
  const { data } = useQuery<Kredit[]>({ queryKey: ["kredite", jahr], queryFn: () => api.get(`/api/kredite/${jahr}`) });
  return (
    <section className="bg-white rounded-iosLg shadow-card p-4">
      <h2 className="text-[15px] font-medium">Kredite</h2>
      <p className="text-[12px] text-ios-muted mb-2">
        Jeder Kredit ist ein Konto der Art „Kredit“ — eine neue Umschuldung unter Einstellungen › Konten anlegen, dann hier eintragen.
        Hausbezogener Anteil = Teil des Kredits, der fürs Haus aufgenommen wurde (wird ins Folgejahr übernommen).
        Zinsquelle und Grundschuld gelten für alle Jahre und sind gesperrt, sobald Werte eingetragen sind. Vorzeitig abgelöst? Datum und Zinsen laut Schlussabrechnung eintragen — danach fragt die App nichts mehr ab.
      </p>
      {(data ?? []).length === 0 && <p className="text-[13px] text-ios-muted">Kein Konto der Art „Kredit“ vorhanden.</p>}
      <div className="divide-y divide-ios-line">{(data ?? []).map((k) => <KreditZeile key={k.kontoId} k={k} jahr={jahr} />)}</div>
    </section>
  );
}
function KreditZeile({ k, jahr }: { k: Kredit; jahr: number }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [hb, setHb] = useState(k.hausbezug === null ? "" : String(Math.round(k.hausbezug * 10000) / 100).replace(".", ","));
  const [hbQ, setHbQ] = useState(k.hausbezugQuelle ?? "");
  const [zi, setZi] = useState(k.zinsen === null ? "" : String(Math.abs(k.zinsen)).replace(".", ","));
  const [ziQ, setZiQ] = useState(k.zinsenQuelle ?? "");
  const fertig = () => { void qc.invalidateQueries({ queryKey: ["kredite"] }); void qc.invalidateQueries({ queryKey: ["steuer"] }); };
  const art = useMutation({ mutationFn: (b: { zinsQuelle?: string | null; grundschuld?: boolean }) => api.put(`/api/kredite/${k.kontoId}/art`, b),
    onSuccess: fertig, onError: (e) => toast.error(fehlertext(e)) });
  const zahl = (t: string) => (t.trim() === "" ? null : Number(t.replace(",", ".")));
  const speichern = useMutation({
    mutationFn: () => api.put(`/api/kredite/${k.kontoId}/${jahr}`, {
      hausbezug: zahl(hb) === null ? null : zahl(hb)! / 100, hausbezugQuelle: hbQ,
      ...(k.zinsQuelle === "tilgungsplan" ? { zinsen: zahl(zi), zinsenQuelle: ziQ } : {}),
    }),
    onSuccess: () => { toast.success(`${k.name} ${jahr} gespeichert`); fertig(); }, onError: (e) => toast.error(fehlertext(e)),
  });
  return (
    <div className="py-3 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[14px] font-medium flex-1 min-w-[8rem]">{k.name}{!k.aktiv && <span className="text-ios-muted"> (inaktiv)</span>}</span>
        <select className={feld} aria-label={`Zinsquelle ${k.name}`} value={k.zinsQuelle ?? ""} onChange={(e) => art.mutate({ zinsQuelle: e.target.value || null })}>
          <option value="">rechnet nicht mit</option>
          <option value="buchungen">Zinsen aus Kontoauszug (Buchungen)</option>
          <option value="tilgungsplan">Zinsen aus Tilgungsplan (von Hand)</option>
        </select>
        <label className="text-[13px] inline-flex items-center gap-1">
          <input type="checkbox" checked={k.grundschuld} onChange={(e) => art.mutate({ grundschuld: e.target.checked })} /> durch Grundschuld gesichert
        </label>
      </div>
      {k.zinsQuelle && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[13px]">
          <label className="flex flex-col gap-0.5">hausbezogener Anteil {jahr} (%)
            <input className={clsx(feld, k.hausbezug === null && "border-ios-blue")} inputMode="decimal" value={hb} onChange={(e) => setHb(e.target.value)} placeholder="z. B. 89,58" />
          </label>
          <label className="flex flex-col gap-0.5">Herkunft
            <input className={feld} value={hbQ} onChange={(e) => setHbQ(e.target.value)} placeholder="z. B. Kreditvertrag, Verwendungsnachweis" />
          </label>
          {k.zinsQuelle === "tilgungsplan" && k.planZinsJahr !== null && (
            <p className="sm:col-span-2 text-[13px]">Zinsen {jahr} aus dem hochgeladenen Tilgungsplan: <b>{eur(k.planZinsJahr)}</b> (automatisch)</p>
          )}
          {k.zinsQuelle === "tilgungsplan" && k.planZinsJahr === null && <>
            <label className="flex flex-col gap-0.5">Zinsen {jahr} laut Tilgungsplan (€)
              <input className={clsx(feld, k.zinsen === null && "border-ios-blue")} inputMode="decimal" value={zi} onChange={(e) => setZi(e.target.value)} placeholder="z. B. 844,51" />
            </label>
            <label className="flex flex-col gap-0.5">Herkunft
              <input className={feld} value={ziQ} onChange={(e) => setZiQ(e.target.value)} placeholder={`z. B. Tilgungsplan ${jahr}`} />
            </label>
          </>}
          <div className="sm:col-span-2">
            <button type="button" disabled={speichern.isPending} onClick={() => speichern.mutate()} className="px-3 py-1.5 rounded-ios bg-ios-blue text-white text-[14px]">Speichern</button>
          </div>
        </div>
      )}
      {k.zinsQuelle && <KreditLaufzeit k={k} />}
      {k.zinsQuelle === "tilgungsplan" && <KreditAbloesung k={k} />}
      {k.zinsQuelle === "tilgungsplan" && <TilgungsplanUpload k={k} />}
    </div>
  );
}

// Laufzeit laut Vertrag: Beginn + Monate → Ende (mit Tilgungsplan bestimmt der Plan das Ende). Außerhalb der Laufzeit fragt die App nichts ab.
function KreditLaufzeit({ k }: { k: Kredit }) {
  const qc = useQueryClient(); const toast = useToast();
  const [b, setB] = useState(k.kreditBeginn ?? ""); const [m, setM] = useState(k.kreditLaufzeitMonate === null ? "" : String(k.kreditLaufzeitMonate));
  const speichern = useMutation({ mutationFn: () => api.put(`/api/kredite/${k.kontoId}/laufzeit`, { beginn: b || null, laufzeitMonate: m ? Number(m) : null }),
    onSuccess: () => { toast.success("Laufzeit gespeichert"); void qc.invalidateQueries({ queryKey: ["kredite"] }); void qc.invalidateQueries({ queryKey: ["steuer"] }); },
    onError: (e) => toast.error(fehlertext(e)) });
  return (
    <div className="flex flex-wrap items-end gap-2 text-[13px]">
      <label className="flex flex-col gap-0.5">Beginn laut Vertrag<input type="date" className={feld} value={b} onChange={(e) => setB(e.target.value)} /></label>
      <label className="flex flex-col gap-0.5">Laufzeit (Monate)<input className={`${feld} w-24`} inputMode="numeric" value={m} onChange={(e) => setM(e.target.value)} placeholder="z. B. 84" /></label>
      <button type="button" onClick={() => speichern.mutate()} className="px-3 py-1.5 rounded-ios bg-white shadow-card text-ios-blue">Laufzeit speichern</button>
      <span className="text-ios-muted">{k.zeitraum.ende ? `läuft bis ${k.zeitraum.ende.split("-").reverse().join(".")}${k.plaene.length ? " (laut Tilgungsplan)" : ""}` : "Ende unbekannt"}</span>
    </div>
  );
}

// Vorzeitige Ablösung: Planraten bis zum Datum zählen, danach nichts mehr; im Ablösejahr kommen die Zinsen laut Schlussabrechnung dazu.
function KreditAbloesung({ k }: { k: Kredit }) {
  const qc = useQueryClient(); const toast = useToast();
  const [am, setAm] = useState(k.abgeloestAm ?? "");
  const [z, setZ] = useState(k.schlusszinsen === null ? "" : String(Math.abs(k.schlusszinsen)).replace(".", ","));
  const [q, setQ] = useState(k.schlusszinsenQuelle ?? "");
  // Ändert die Ablösung ein abgeschlossenes Jahr, kommt 409 mit der Liste → ausdrücklich bestätigen, sonst nichts speichern
  const [warnung, setWarnung] = useState<{ text: string; leeren: boolean } | null>(null);
  const speichern = useMutation({ mutationFn: ({ leeren, bestaetigt }: { leeren: boolean; bestaetigt?: boolean }) => api.put(`/api/kredite/${k.kontoId}/abloesung`,
      { ...(leeren ? { am: null } : { am: am || null, schlusszinsen: z.trim() === "" ? null : z, quelle: q }), vergangeneBestaetigt: !!bestaetigt }),
    onSuccess: (_d, { leeren }) => { setWarnung(null); if (leeren) { setAm(""); setZ(""); setQ(""); } toast.success(leeren ? "Ablösung entfernt" : "Ablösung gespeichert");
      void qc.invalidateQueries({ queryKey: ["kredite"] }); void qc.invalidateQueries({ queryKey: ["steuer"] }); },
    onError: (e, { leeren }) => { if (e instanceof ApiError && e.status === 409) setWarnung({ text: e.message, leeren }); else toast.error(fehlertext(e)); } });
  return (
    <div className="flex flex-wrap items-end gap-2 text-[13px]">
      <label className="flex flex-col gap-0.5">vorzeitig abgelöst am<input type="date" className={feld} value={am} onChange={(e) => setAm(e.target.value)} /></label>
      {am && <>
        <label className="flex flex-col gap-0.5">Zinsen laut Schlussabrechnung (€)
          <input className={clsx(feld, "w-32", k.abgeloestAm && k.schlusszinsen === null && "border-ios-blue")} inputMode="decimal" value={z} onChange={(e) => setZ(e.target.value)} placeholder="z. B. 12,34" />
        </label>
        <label className="flex flex-col gap-0.5">Herkunft<input className={feld} value={q} onChange={(e) => setQ(e.target.value)} placeholder="z. B. Schlussabrechnung vom …" /></label>
      </>}
      <button type="button" onClick={() => speichern.mutate({ leeren: false })} className="px-3 py-1.5 rounded-ios bg-white shadow-card text-ios-blue">Ablösung speichern</button>
      {k.abgeloestAm && <button type="button" onClick={() => speichern.mutate({ leeren: true })} className="px-3 py-1.5 rounded-ios bg-white shadow-card text-ios-muted">Ablösung entfernen</button>}
      {warnung && (
        <div className="basis-full rounded-lg bg-ios-blue/10 p-2 text-ios-blue">
          {warnung.text}{" "}
          <button type="button" className="underline" onClick={() => speichern.mutate({ leeren: warnung.leeren, bestaetigt: true })}>trotzdem speichern</button>{" "}
          <button type="button" className="underline text-ios-muted" onClick={() => setWarnung(null)}>abbrechen</button>
        </div>
      )}
    </div>
  );
}

// Tilgungsplan als PDF: erst lesen und prüfen (Summen = Aufdruck), dann übernehmen. Neuer Plan bei Konditionsänderung gilt ab Datum.
function TilgungsplanUpload({ k }: { k: Kredit }) {
  const qc = useQueryClient(); const toast = useToast();
  const [datei, setDatei] = useState<File | null>(null); const [ab, setAb] = useState(""); const [v, setV] = useState<PlanVorschau | null>(null);
  const senden = async (speichern: boolean) => {
    if (!datei) return;
    const fd = new FormData(); fd.append("datei", datei); if (ab) fd.append("gueltigAb", ab);
    if (speichern) { fd.append("speichern", "1"); if ((v?.aenderungen ?? []).length) fd.append("vergangeneBestaetigt", "1"); }
    const r = await fetch(`/api/kredite/${k.kontoId}/tilgungsplan`, { method: "POST", body: fd, credentials: "same-origin" });
    const j = await r.json();
    if (!r.ok) { toast.error(j.error ?? "Fehler"); return; }
    setV(j);
    if (speichern) { toast.success("Tilgungsplan übernommen"); setDatei(null); setV(null); void qc.invalidateQueries({ queryKey: ["kredite"] }); void qc.invalidateQueries({ queryKey: ["steuer"] }); }
  };
  const loeschen = useMutation({ mutationFn: (id: string) => api.del(`/api/kredite/${k.kontoId}/tilgungsplan/${id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["kredite"] }); void qc.invalidateQueries({ queryKey: ["steuer"] }); } });
  return (
    <div className="text-[13px] space-y-1.5">
      {k.plaene.map((p) => (
        <div key={p.id} className="flex items-center gap-2">
          <span className="flex-1">Plan ab {p.gueltigAb.split("-").reverse().join(".")}: {p.quelle} — Zinsen gesamt {eur(p.summeZinsen)}</span>
          <button type="button" onClick={() => loeschen.mutate(p.id)} title="Plan entfernen" className="text-ios-muted hover:text-ios-red"><TrashIcon className="w-4 h-4" /></button>
        </div>
      ))}
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-0.5">{k.plaene.length ? "Neuer Plan (z. B. Konditionsänderung)" : "Tilgungsplan (PDF)"}
          <input type="file" accept="application/pdf" onChange={(e) => { setDatei(e.target.files?.[0] ?? null); setV(null); }} /></label>
        {k.plaene.length > 0 && <label className="flex flex-col gap-0.5">gilt ab<input type="date" className={feld} value={ab} onChange={(e) => setAb(e.target.value)} /></label>}
        <button type="button" disabled={!datei} onClick={() => void senden(false)} className="px-3 py-1.5 rounded-ios bg-white shadow-card text-ios-blue">Plan prüfen</button>
      </div>
      {v && (
        <div className="rounded-ios bg-ios-bg/60 p-2 space-y-1">
          <p>{v.zeilen} Raten von {v.erste?.datum.split("-").reverse().join(".")} bis {v.letzte?.datum.split("-").reverse().join(".")}; Zinsen {eur(v.summeZinsen)}
            {v.gedruckt.zinsenCent !== null && ` (aufgedruckt ${eur(v.gedruckt.zinsenCent / 100)})`}, Tilgung {eur(v.summeTilgung)}
            {v.gedruckt.nettoCent !== null && ` (Nettokredit ${eur(v.gedruckt.nettoCent / 100)})`}</p>
          <p className="text-ios-muted">{Object.entries(v.jeJahr).map(([j, x]) => `${j}: ${eur(x.zinsCent / 100)}`).join(" · ")}</p>
          {(v.aenderungen ?? []).length > 0 && (
            <p className="text-ios-blue">Achtung, der neue Plan ändert den Jahreszins: {v.aenderungen!.map((a) => `${a.jahr}: ${eur(a.vorher)} → ${eur(a.nachher)}`).join(" · ")}.
              Liegt ein bereits abgegebenes Jahr darunter, „gilt ab“ prüfen.</p>
          )}
          {k.plaene.length > 0 && !ab && <p className="text-ios-blue">Bitte „gilt ab“ angeben (Datum der Konditionsänderung).</p>}
          {v.fehler.length ? <p className="text-ios-blue">Nicht übernehmbar: {v.fehler.join("; ")}</p>
            : <button type="button" onClick={() => void senden(true)} className="px-3 py-1.5 rounded-ios bg-ios-blue text-white">Summen stimmen — Plan übernehmen</button>}
        </div>
      )}
    </div>
  );
}

// Beteiligungen (27.10.2026): nur im Zeitraum ab…bis aktiv; außerhalb fragt die App nichts ab und die Steuer-Seite zeigt keine Zeile.
type Bet = { id: string; name: string; abJahr: number | null; bisJahr: number | null; quelle: string | null; aktiv: boolean; einkuenfte: number | null; einkuenfteQuelle: string | null };
function Beteiligungen({ jahr }: { jahr: number }) {
  const qc = useQueryClient(); const toast = useToast();
  const { data } = useQuery<Bet[]>({ queryKey: ["beteiligungen", jahr], queryFn: () => api.get(`/api/beteiligungen/${jahr}`) });
  const [neu, setNeu] = useState({ name: "", ab: String(jahr), bis: "", quelle: "" });
  const anlegen = useMutation({ mutationFn: () => api.post("/api/beteiligungen", { name: neu.name, abJahr: neu.ab, bisJahr: neu.bis, quelle: neu.quelle }),
    onSuccess: () => { toast.success("Beteiligung angelegt"); setNeu({ name: "", ab: String(jahr), bis: "", quelle: "" }); void qc.invalidateQueries({ queryKey: ["beteiligungen"] }); void qc.invalidateQueries({ queryKey: ["steuer"] }); },
    onError: (e) => toast.error(fehlertext(e)) });
  const aktiv = (data ?? []).filter((b) => b.aktiv), andere = (data ?? []).filter((b) => !b.aktiv);
  return (
    <section className="bg-white rounded-iosLg shadow-card p-4">
      <h2 className="text-[15px] font-medium">Beteiligungen</h2>
      <p className="text-[12px] text-ios-muted mb-2">
        Einkünfte laut gesonderter Feststellung (z. B. Erbengemeinschaft). Abgefragt nur für Beteiligungen, die {jahr} bestehen (Zeitraum ab–bis).
      </p>
      {aktiv.length === 0 && <p className="text-[13px] text-ios-muted">Keine Beteiligung in {jahr}.</p>}
      <div className="divide-y divide-ios-line">{aktiv.map((b) => <BetZeile key={b.id} b={b} jahr={jahr} />)}</div>
      {andere.length > 0 && <p className="text-[12px] text-ios-muted mt-2">Nicht in {jahr}: {andere.map((b) => `${b.name} (${b.abJahr ?? "…"}–${b.bisJahr ?? "…"})`).join(", ")}</p>}
      <details className="mt-2 text-[13px]">
        <summary className="text-ios-blue cursor-pointer">Beteiligung anlegen</summary>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
          <input className={clsx(feld, "col-span-2")} placeholder="Name" value={neu.name} onChange={(e) => setNeu({ ...neu, name: e.target.value })} />
          <input className={feld} inputMode="numeric" placeholder="ab Jahr" value={neu.ab} onChange={(e) => setNeu({ ...neu, ab: e.target.value })} />
          <input className={feld} inputMode="numeric" placeholder="bis Jahr (leer = läuft)" value={neu.bis} onChange={(e) => setNeu({ ...neu, bis: e.target.value })} />
          <input className={clsx(feld, "col-span-2 sm:col-span-3")} placeholder="Herkunft (z. B. Gesellschaftsvertrag)" value={neu.quelle} onChange={(e) => setNeu({ ...neu, quelle: e.target.value })} />
          <button type="button" disabled={!neu.name.trim() || anlegen.isPending} onClick={() => anlegen.mutate()} className="px-3 py-1 rounded-ios bg-ios-blue text-white disabled:opacity-40">Anlegen</button>
        </div>
      </details>
    </section>
  );
}
function BetZeile({ b, jahr }: { b: Bet; jahr: number }) {
  const qc = useQueryClient(); const toast = useToast();
  const [v, setV] = useState(b.einkuenfte === null ? "" : String(b.einkuenfte).replace(".", ","));
  const [q, setQ] = useState(b.einkuenfteQuelle ?? "");
  const [bis, setBis] = useState(b.bisJahr === null ? "" : String(b.bisJahr));
  const fertig = () => { void qc.invalidateQueries({ queryKey: ["beteiligungen"] }); void qc.invalidateQueries({ queryKey: ["steuer"] }); };
  const speichern = useMutation({ mutationFn: () => api.put(`/api/beteiligungen/${b.id}/${jahr}`, { einkuenfte: v.trim() === "" ? null : Number(v.replace(/\./g, "").replace(",", ".")), quelle: q }),
    onSuccess: () => { toast.success(`${b.name} ${jahr} gespeichert`); fertig(); }, onError: (e) => toast.error(fehlertext(e)) });
  const ende = useMutation({ mutationFn: () => api.post("/api/beteiligungen", { id: b.id, name: b.name, abJahr: b.abJahr, bisJahr: bis, quelle: b.quelle }),
    onSuccess: () => { toast.success("Zeitraum gespeichert"); fertig(); }, onError: (e) => toast.error(fehlertext(e)) });
  return (
    <div className="py-3 space-y-2 text-[13px]">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[14px] font-medium flex-1">{b.name}</span>
        {b.einkuenfte === null && <span className="text-[12px] px-2 py-0.5 rounded-full bg-ios-blue/10 text-ios-blue">fehlt</span>}
      </div>
      <div className="flex flex-wrap gap-2">
        <input className={clsx(feld, "w-40", b.einkuenfte === null && "border-ios-blue")} inputMode="decimal" placeholder={`Einkünfte ${jahr} (€)`} value={v} onChange={(e) => setV(e.target.value)} />
        <input className={clsx(feld, "flex-1 min-w-[10rem]")} placeholder="Herkunft (Feststellungsbescheid)" value={q} onChange={(e) => setQ(e.target.value)} />
        <button type="button" disabled={speichern.isPending} onClick={() => speichern.mutate()} className="px-3 py-1 rounded-ios bg-ios-blue text-white">Speichern</button>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-ios-muted">
        besteht ab {b.abJahr ?? "—"} bis
        <input className={clsx(feld, "w-24")} inputMode="numeric" placeholder="offen" value={bis} onChange={(e) => setBis(e.target.value)} />
        <button type="button" disabled={ende.isPending || bis === (b.bisJahr === null ? "" : String(b.bisJahr))} onClick={() => ende.mutate()} className="text-ios-blue disabled:opacity-40">Zeitraum speichern</button>
        {b.quelle && <span>· {b.quelle}</span>}
      </div>
    </div>
  );
}
